import { Autocomplete, Box, Chip, CircularProgress, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material'
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import CardSkeleton from './CardSkeleton'
import NoDataAvailableCard from './NoDataAvailableCard'
import StatTable from './StatTable'
import GraphCard from './GraphCard'
import { globalGetService } from '../../../utils/globalApiServices'
import { checkApiStatus } from '../../../utils'
import { assetType as assetTypeMap, dateMonthYearDateFormat } from '../../../constants'
import { getMaintenanceEventApi, getUtilizationAssetsApi } from '../apiService'
import moment from 'moment'
import CardFilter from './CardFilter'

const AMOUNT_TYPE_COLORS = {
    neutral: '#94A3B8',
    inflow: '#16A34A',
    outflow: '#DC2626',
    total: '#000000',
}

const formatCurrencyAmount = (value, currency = 'USD') => {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value || 0)
    } catch {
        return `${currency} ${Number(value || 0).toLocaleString()}`
    }
}

const MaintenanceReserveFunds = ({ maintenanceData, isLoading, eventType, onEventTypeChange, filters = {}, onFilterChange, currencies = [], cardFiltersDisabled = false }) => {
    const params = useParams();
    const [assetOptions, setAssetOptions] = useState([])
    const [assetOptionsLoading, setAssetOptionsLoading] = useState(false)
    const [selectedAsset, setSelectedAsset] = useState(null)
    const [maintenanceEvents, setMaintenanceEvents] = useState([])
    const [maintenanceEventsLoading, setMaintenanceEventsLoading] = useState(false)
    const [isMRDrillDown, setMRDrillDown] = useState(false)
    const [selectedEventType, setSelectedEventType] = useState(null)

    const fundMovementData = maintenanceData?.fund_movement_waterfall
    const currency = fundMovementData?.filters?.currency || 'USD'

    const mrFundBalance = maintenanceData?.mr_fund_balance_by_event_type
    const mrFundBalanceOptions = useMemo(() => (
        mrFundBalance?.options
            ? { ...mrFundBalance.options, xAxis: { ...mrFundBalance.options.xAxis, triggerEvent: true, axisLabel: { ...mrFundBalance.options.xAxis?.axisLabel, triggerEvent: true } } }
            : null
    ), [mrFundBalance])

    useEffect(() => {
        setSelectedEventType(null);
        if (!mrFundBalanceOptions) setMRDrillDown(false);
    }, [mrFundBalanceOptions])

    const handleEventTypeClick = (chartParams) => {
        if (chartParams?.dataIndex == null) return
        const row = mrFundBalance?.table?.rows?.[chartParams?.dataIndex]
        if (!row) return
        setSelectedEventType(row?.event_type_id)
        setMRDrillDown(true)
    }

    const mrDrillDownRows = useMemo(() => (
        selectedEventType != null
            ? (mrFundBalance?.table?.rows || []).filter(row => row?.event_type_id === selectedEventType)
            : mrFundBalance?.table?.rows
    ), [selectedEventType, mrFundBalance])

    const selectedEventTypeLabel = useMemo(() => (
        mrFundBalance?.table?.rows?.find(row => row?.event_type_id === selectedEventType)?.event_type
    ), [selectedEventType, mrFundBalance])

    const getMaintenanceEvents = (asset) => {
        setMaintenanceEventsLoading(true)
        globalGetService(getMaintenanceEventApi(assetTypeMap[asset?.asset_type], asset?.id)).then(response => {
            setMaintenanceEventsLoading(false)
            if (checkApiStatus(response)) {
                const events = asset?.asset_type === 4 && asset?.is_grouped ? response?.data?.data || [] : response?.data?.data?.[0]?.maintenance_events || []
                setMaintenanceEvents(events)
                onEventTypeChange?.(events?.[0]?.id || '')
            }
        })
    }

    const onAssetChange = (value) => {
        setSelectedAsset(value)
        setMaintenanceEvents([])
        if (value?.asset_type && value?.id) getMaintenanceEvents(value)
    }

    useEffect(() => {
        setAssetOptionsLoading(true)
        globalGetService(getUtilizationAssetsApi(params)).then(response => {
            setAssetOptionsLoading(false)
            if (checkApiStatus(response)) {
                const options = response?.data?.data || []
                setAssetOptions(options)
                if (options.length) onAssetChange(options[0])
            }
        })
    }, [])

    const movementColumns = useMemo(() => ([
        {
            field: 'movement',
            label: 'Movement',
            render: (row) => (
                <span className={row?.is_total ? 'mrf-amount-total' : ''}>
                    {row?.movement}
                </span>
            ),
        },
        {
            field: 'amount',
            label: 'Amount',
            render: (row) => (
                <span className={row?.is_total ? 'mrf-amount-total' : ''} style={{ color: AMOUNT_TYPE_COLORS[row?.amount_type] || AMOUNT_TYPE_COLORS.neutral }}>
                    {formatCurrencyAmount(row?.amount, currency)}
                </span>
            ),
        },
    ]), [currency])

    return (
        <div className="contract-analytics-wrapper">
            <div className="ca-page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontSize='15px' fontFamily="Roboto-SemiBold !important">Maintenance Reserve Funds </Typography>
                <Autocomplete
                    disablePortal
                    disableClearable
                    sx={{
                        width: 180,
                        '& .MuiOutlinedInput-root': { height: 34, fontSize: '12px' },
                        '& .MuiOutlinedInput-input': { padding: '2px 4px !important' },
                    }}
                    options={assetOptions}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    disabled={assetOptionsLoading}
                    getOptionLabel={(option) => `${option?.asset_name || ''} ${option?.name || ''}` || ""}
                    noOptionsText={assetOptionsLoading ? "loading" : "No Option Available"}
                    value={selectedAsset}
                    onChange={(e, value) => onAssetChange(value)}
                    renderInput={(params) => {
                        const selectedLabel = params?.inputProps?.value || "";
                        return (
                            <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow>
                                <TextField
                                    {...params}
                                    margin='none'
                                    size='small'
                                    fullWidth
                                    placeholder='Select Asset'
                                    disabled={assetOptionsLoading}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: {
                                            ...params?.InputProps, endAdornment: (<>{assetOptionsLoading ? (<CircularProgress color="inherit" size={20} />) : null}{params?.InputProps?.endAdornment}</>)
                                        }
                                    }}
                                />
                            </Tooltip>
                        );
                    }}
                />
            </div>
            <Grid container spacing={1} mx={1} sx={{ alignItems: 'stretch' }}>
                <Grid size={{ md: 8, xs: 12 }} sx={{ display: 'flex' }}>
                    <Paper className="ca-util-card mr-equal-card">
                        <CardFilter
                            disabled={cardFiltersDisabled}
                            title={<> MR Fund Balance by Event Type <span className="ca-header-subtext" style={{ marginLeft: '10px' }}> {mrFundBalance?.as_of ? `As of ${moment(mrFundBalance?.as_of).format(dateMonthYearDateFormat)}` : ''}</span> </>}
                            filter={filters.mr_fund_balance_by_event_type}
                            onChange={(filter) => onFilterChange('mr_fund_balance_by_event_type', filter)}
                            currencyOptions={currencies}
                            actions={mrFundBalance?.table?.rows?.length > 0 &&
                                <Tooltip title={isMRDrillDown ? "Hide drill down" : "Drill down"}>
                                    <IconButton
                                        size='small'
                                        color={isMRDrillDown ? 'primary' : 'default'}
                                        onClick={() => {
                                            if (isMRDrillDown) setSelectedEventType(null);
                                            setMRDrillDown(!isMRDrillDown);
                                        }}
                                    >
                                        <UnfoldMoreOutlinedIcon fontSize='small' />
                                    </IconButton>
                                </Tooltip>}
                        />
                        {isLoading?.mr_fund_balance_by_event_type ? (
                            <CardSkeleton height='220px' className='ca-graph-container' />
                        ) : (
                            <div className="ca-card-body">
                                <div className="ca-graph-container">
                                    {!mrFundBalanceOptions ? (
                                        <NoDataAvailableCard title='No MR Fund Balance data available' subtext='Try adjusting the date range' />
                                    ) : (
                                        <GraphCard option={mrFundBalanceOptions} height='250px' onChartClick={handleEventTypeClick} widenClickArea/>
                                    )}
                                </div>
                                {isMRDrillDown &&
                                    <div className="ca-drilldown">
                                        {selectedEventTypeLabel &&
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
                                                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Showing:</Typography>
                                                <Chip label={selectedEventTypeLabel} size="small" onDelete={() => setSelectedEventType(null)} />
                                            </Box>}
                                        {mrDrillDownRows?.length ? (
                                            <div className="ca-drilldown-scroll">
                                                <StatTable columns={mrFundBalance?.table?.columns} rows={mrDrillDownRows} />
                                            </div>
                                        ) : selectedEventTypeLabel ? (
                                            <NoDataAvailableCard title={`No data available for ${selectedEventTypeLabel}`} subtext='Try selecting another event type' />
                                        ) : (
                                            <NoDataAvailableCard title='No event type data available' subtext='Try adjusting the date range' />
                                        )}
                                    </div>}
                            </div>
                        )}
                    </Paper>
                </Grid>
                <Grid size={{ md: 4, xs: 12 }} sx={{ display: 'flex' }}>
                    <Paper className="mr-equal-card">
                        <CardFilter
                            disabled={cardFiltersDisabled}
                            title="Fund Movement Waterfall"
                            filter={filters.fund_movement_waterfall}
                            onChange={(filter) => onFilterChange('fund_movement_waterfall', filter)}
                            currencyOptions={currencies}
                            actions={
                                <div className="ca-header-actions">
                                    <Autocomplete
                                        disablePortal
                                        disableClearable
                                        size="small"
                                        sx={{
                                            width: 160,
                                            '& .MuiOutlinedInput-root': { height: 26, fontSize: '11px' },
                                            '& .MuiOutlinedInput-input': { padding: '2px 4px !important' },
                                        }}
                                        options={maintenanceEvents}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        disabled={isLoading?.fund_movement_waterfall || maintenanceEventsLoading }
                                        getOptionLabel={(option) => option?.name || ''}
                                        noOptionsText={maintenanceEventsLoading ? 'Loading...' : 'No Options'}
                                        value={maintenanceEvents.find((event) => event?.id === eventType) || null}
                                        onChange={(e, value) => onEventTypeChange?.(value?.id || '')}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                margin='none'
                                                size='small'
                                                fullWidth
                                                placeholder='Select Event Type'
                                                disabled={isLoading?.fund_movement_waterfall || maintenanceEventsLoading }
                                                slotProps={{
                                                    inputLabel: { shrink: true },
                                                    input: {
                                                        ...params?.InputProps, endAdornment: (<>{maintenanceEventsLoading ? (<CircularProgress color="inherit" size={16} />) : null}{params?.InputProps?.endAdornment}</>)
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            }
                        />
                        {isLoading?.fund_movement_waterfall || maintenanceEventsLoading ? (
                            <CardSkeleton />
                        ) : (
                                <div className="ca-claim-block ca-card-body" >
                                 {!fundMovementData?.rows?.length || !maintenanceEvents?.length ? (
                                    <NoDataAvailableCard title='No Fund Movement Waterfall data available' subtext='Try adjusting the date range' />
                                ) : (
                                    <div className='clm-aprv-trnard ca-scroll-table' >
                                        <StatTable columns={movementColumns} rows={fundMovementData.rows}  />
                                    </div>
                                )}
                            </div>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </div>
    )
}

export default MaintenanceReserveFunds
